import axios from 'axios';
import Cookies from 'universal-cookie';
import jwt_decode from 'jwt-decode';
import { useQuery } from '@tanstack/react-query';
import { queryClient } from 'components/providers/QueryProvider';

export type AuthUser = {
  userId: string | number;
  email: string;
  roles: string[];
};
export type NusSession = {
  csrfToken: string;
} & (
  | { status: 'authenticated'; user: AuthUser }
  | { status: 'profile_required'; email: string }
);

type AuthState = {
  ready: boolean;
  session: NusSession | null;
  user: AuthUser | null;
};

const initialState: AuthState = {
  ready: false,
  session: null,
  user: null
};
const authKey = ['authentication'];
const adminRoles = [
  'ROLE_ADMIN',
  'ROLE_SUB_ADMIN',
  'ROLE_BUOY_MAINTAINER',
  'ROLE_DEVELOPER_ADMIN',
  'ROLE_DEVELOPER'
];
export const hasAdminAccess = (user: AuthUser | null) =>
  !!user?.roles.some((role) => adminRoles.includes(role));

// Preserve a selected role only while it still belongs to the current account.
export function getActiveRole(user = getAuth().user): string | undefined {
  const selected = sessionStorage.getItem('currentRole');
  return selected &&
    adminRoles.includes(selected) &&
    user?.roles.includes(selected)
    ? selected
    : adminRoles.find((role) => user?.roles.includes(role));
}

const cookies = new Cookies();
let loading: Promise<NusSession | null> | undefined;

// Decode existing password tokens for UI state; the backend validates API access.
function passwordUser(): AuthUser | null {
  const tokens = cookies.get('admin-tokens');
  try {
    return tokens ? jwt_decode<AuthUser>(tokens.accessToken) : null;
  } catch {
    cookies.remove('admin-tokens', { path: '/' });
    return null;
  }
}

function publish(session: NusSession | null) {
  const user = session
    ? session.status === 'authenticated'
      ? session.user
      : null
    : passwordUser();
  const role = getActiveRole(user);
  if (role) sessionStorage.setItem('currentRole', role);
  else sessionStorage.removeItem('currentRole');
  queryClient.setQueryData<AuthState>(authKey, {
    ready: true,
    session,
    user
  });
}

export const getAuth = () =>
  queryClient.getQueryData<AuthState>(authKey) ?? initialState;
// Subscribe to server session state using the existing query cache, without a separate state store.
// Query cache reference: https://tanstack.com/query/latest/docs/reference/QueryClient#queryclientsetquerydata
export const useAuth = () =>
  useQuery({
    queryKey: authKey,
    queryFn: getAuth,
    initialData: initialState,
    enabled: false,
    staleTime: Infinity
  }).data;

// Read the HttpOnly session through the backend. Keep claims and CSRF state in memory only.
// Cookie request configuration: https://axios-http.com/docs/req_config
export function loadSession(force = false): Promise<NusSession | null> {
  // Share an in-flight lookup; force reloads the rotated session after sign-in.
  if (loading) return loading;
  if (getAuth().ready && !force) return Promise.resolve(getAuth().session);
  // Use Axios directly because the shared API interceptor waits for this lookup.
  loading = axios
    .get<NusSession>(`${import.meta.env.VITE_API_URL}/public/oidc/session`, {
      withCredentials: true,
      timeout: 10000
    })
    .then(({ data }) => {
      if (
        !['authenticated', 'profile_required'].includes(data.status) ||
        !data.csrfToken ||
        (data.status === 'authenticated'
          ? !data.user?.userId || !Array.isArray(data.user.roles)
          : !data.email)
      ) {
        throw new Error('Invalid sign-in response');
      }
      cookies.remove('admin-tokens', { path: '/' });
      publish(data);
      return data;
    })
    .catch((error) => {
      if (
        force &&
        (!axios.isAxiosError(error) || error.response?.status !== 401)
      ) {
        throw error;
      }
      publish(null);
      return null;
    })
    .finally(() => {
      loading = undefined;
    });
  return loading;
}

export function passwordSignedIn(tokens: {
  accessToken: string;
  refreshToken: string;
}) {
  cookies.set('admin-tokens', tokens, {
    path: '/',
    secure: true,
    sameSite: true
  });
  publish(null);
}

export function clearAuth() {
  cookies.remove('admin-tokens', { path: '/' });
  publish(null);
}
